import { useLocation } from 'react-router-dom'
import { useLayout } from '@context/LayoutContext'
import { useDeviceType } from './useDeviceType'

/**
 * Determine layout visibility (header/footer) based on route and device type.
 *
 * Architecture:
 * - Router = SSoT for base layout (which route determines default visibility)
 * - LayoutContext = Optional overrides (game screens hide chrome via useLayoutVisibility)
 * - Device type = Conditional footer visibility
 *
 * Returns route type for App.jsx routing decisions.
 * Does NOT handle game flow state (start/game/end) - that's orchestrator responsibility.
 *
 * @param {string|null} currentPageOverride - Override for static pages (from StaticPageHandler)
 * @returns {{ currentScreen: string, showHeader: boolean, showFooter: boolean }}
 */

export function useAppLayout (currentPageOverride = null) {
  const location = useLocation()
  const { isTabletHorizontalOrDesktop } = useDeviceType()
  const { headerOverride, footerOverride } = useLayout()

  // Static pages override (e. g., /about, /privacy, etc.)
  if (currentPageOverride) {
    return {
      currentScreen: currentPageOverride,
      showHeader: headerOverride ?? true,
      showFooter: footerOverride ?? isTabletHorizontalOrDesktop
    }
  }

  // Parse route (Router = SSoT for base layout)
  const segments = location.pathname.split('/').filter(Boolean) // Remove empty segments
  // segments[0] = lang ('es', 'gl', etc.)
  // segments[1] = routeType ('puzzle', undefined for current /:lang)
  const routeType = segments[1] || 'home'

  // Base layout configuration per route
  const baseLayout = {
    home: { header: true, footer: isTabletHorizontalOrDesktop },
    memorama: { header: true, footer: isTabletHorizontalOrDesktop },
    puzzle: { header: true, footer: isTabletHorizontalOrDesktop }
  }

  const config = baseLayout[routeType] || baseLayout.home

  // Apply imperative overrides (opt in from game screens)
  // Game screens will call useLayoutVisibility({ header: false, footer: false })
  return {
    currentScreen: currentPageOverride,
    showHeader: headerOverride ?? config.header,
    showFooter: footerOverride ?? config.footer
  }
}
